import React from 'react'

import { useBreakpoint } from 'slab'

interface WelcomeWrapperProps {
  children: React.ReactNode
  logoImagePath: string
  logoAllCapImagePath: string
}

const wrapperClassNames = 'w-screen h-screen bg-gradient-to-t from-thatch-100 to-gray-50 bg-fixed'
const innerContainerClassNames = 'w-full h-full bg-gray-50/80 backdrop-blur-md'
const purpleLinesClass = 'bg-[repeating-linear-gradient(45deg,theme(colors.thatch.300)_0px,theme(colors.thatch.300)_2px,transparent_2px,transparent_12px)]'

const imageDiv = ({ logoImagePath, logoAllCapImagePath }: { logoImagePath: string, logoAllCapImagePath: string }) => (
  <div className='flex items-center justify-center gap-2 p-2 border-b border-thatch-300'>
    <img src={logoImagePath} alt='Thatch logo' className='h-8' />
    <img src={logoAllCapImagePath} alt='Thatch' className='h-14' />
  </div>
)

export const WelcomeWrapper: React.FC<WelcomeWrapperProps> = (
  {
    children,
    logoImagePath,
    logoAllCapImagePath
  }
) => {
  const { breakpointIsAtLeast } = useBreakpoint()
  const shouldRenderGrid = breakpointIsAtLeast('md')

  return (
    <div className={wrapperClassNames}>
      <div
        className={`${innerContainerClassNames} ${shouldRenderGrid ? 'grid' : 'flex flex-col min-h-screen'}`}
        style={shouldRenderGrid
          ? {
              gridTemplateColumns: '210px 1fr 80px',
              gridTemplateRows: '80px auto 50px'
            }
          : undefined}
      >

        {/* Header/Logo section */}
        {imageDiv({ logoImagePath, logoAllCapImagePath })}

        {shouldRenderGrid && (
          <>
            <div className='border-l border-b border-thatch-300' />
            <div className='border-l border-b border-thatch-300' />
            <div className='border-b border-thatch-300' />
          </>
        )}

        {/* Main content area - this container is stable across breakpoints */}
        <div className={shouldRenderGrid
          ? 'flex flex-col justify-center border-l border-b border-thatch-300'
          : 'flex-1 flex flex-col'}
        >
          <div className={shouldRenderGrid ? 'flex justify-center' : 'flex-1 flex flex-col'}>
            {/* This marginLeft offset is to visually center the child content on the page, not just within the center cell: (210 - 80) = 130 / 2 = 65% */}
            {/* This 77% max width is to ensure the child content doesn't extend beyond the center cell:
                  - (768 - 210 - 80) = 468px width for center cell at md breakpoint (768px / 48rem)
                  - (468 - 65) = 403px max width for child content accounting for -65px left margin
                  - (403 - 40) = 363px to allow for additional margin from the left side of the center cell
                  - 363/468 = 77%
            */}
            <div
              className={shouldRenderGrid
                ? 'w-fit max-w-[77%]'
                : 'flex-1 flex flex-col'} style={shouldRenderGrid
                  ? {
                      marginLeft: '-65px'
                    }
                  : undefined}
            >
              <div className={shouldRenderGrid ? 'py-12' : 'p-12 flex-1 border-thatch-300'}>
                {children}
              </div>
              {!shouldRenderGrid && (
                <div className={`h-16 border-t ${purpleLinesClass}`} />
              )}
            </div>
          </div>
        </div>

        {shouldRenderGrid &&
          (
            <>
              <div className={`border-l border-b border-thatch-300 ${purpleLinesClass}`} />
              <div className={purpleLinesClass} />
              <div className='border-l border-thatch-300' />
              <div className='border-l border-thatch-300' />
            </>
          )}
      </div>
    </div>
  )
}

export default WelcomeWrapper
